// How a card maps to its artwork file. This mirrors games/fragments/cards/CardImages.java, and it
// is the one thing the web client legitimately keeps a copy of: it is a naming convention for
// files, not a game rule. The images themselves are not duplicated - the build copies them out of
// app/src/main/res/drawable-nodpi/ - and scripts/copy-cards.mjs checks at build time that every
// name this module can produce actually resolves to a file, so a renamed asset breaks the build
// instead of leaving a blank space at the table.
//
// Everything a card MEANS (its name, its rank, what beats what) comes from the server catalogue
// instead. Only the file name and the shape of the paper live here.

const ITALIAN_VALUES = {
  1: 'asso',
  2: 'due',
  3: 'tre',
  4: 'quattro',
  5: 'cinque',
  6: 'sei',
  7: 'sette',
  8: 'otto',
  9: 'nove',
  10: 'dieci',
  11: 'fante',
  12: 'cavallo',
  13: 're',
};

// The number cards keep the italian names, so one set of file names covers both families; only the
// figures change
const FRENCH_VALUES = { ...ITALIAN_VALUES, 11: 'jack', 12: 'queen', 13: 'king' };

const ITALIAN_SEEDS = { 1: 'denari', 2: 'bastoni', 3: 'coppe', 4: 'spade' };
const FRENCH_SEEDS = { 1: 'cuori', 2: 'quadri', 3: 'fiori', 4: 'picche' };

const FORTY_CARD_VALUES = [1, 2, 3, 4, 5, 6, 7, 11, 12, 13];
const FULL_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

// The shape of each deck's cards and what artwork it has. The aspect ratios are the ones of the
// PNG canvases, the same numbers PlayingCardView uses: the trevisane cards are much narrower than
// the others, and a single ratio makes one of the decks look stretched.
// Trevisane came first and its files have no prefix. The napoletane deck exists only as forty
// cards, so eight, nine, ten and the jokers simply have no artwork there; only the trevisane deck
// has a scan of its back, the others fall back to the drawn one
const DECKS = {
  TREVISANE: {
    prefix: '',
    french: false,
    values: FULL_VALUES,
    jokers: true,
    back: true,
    aspect: 230 / 560,
  },
  NAPOLETANE: {
    prefix: 'nap_',
    french: false,
    values: FORTY_CARD_VALUES,
    jokers: false,
    back: false,
    aspect: 450 / 743,
  },
  FRENCH: {
    prefix: 'fr_',
    french: true,
    values: FULL_VALUES,
    jokers: true,
    back: false,
    aspect: 400 / 600,
  },
};

export const CARDS_PATH = 'cards';
export const JOKER_CARD_TYPE = 0;

// null when this deck has no artwork for this card, so the caller can fall back to its name
export function cardFileName(deckType, seed, cardType) {
  const deck = DECKS[deckType];
  if (!deck) return null;
  if (cardType === JOKER_CARD_TYPE) {
    return deck.jokers ? `${deck.prefix}matta_rossa` : null;
  }
  if (!deck.values.includes(cardType)) return null;
  const value = (deck.french ? FRENCH_VALUES : ITALIAN_VALUES)[cardType];
  const seedName = (deck.french ? FRENCH_SEEDS : ITALIAN_SEEDS)[seed];
  if (!value || !seedName) return null;
  return `${deck.prefix}${value}_di_${seedName}`;
}

export function cardImageUrl(deckType, seed, cardType) {
  const name = cardFileName(deckType, seed, cardType);
  return name ? `./${CARDS_PATH}/${name}.png` : null;
}

// null when nobody scanned the back of this deck, so the caller draws the generic one
export function cardBackFileName(deckType) {
  const deck = DECKS[deckType];
  return deck?.back ? `${deck.prefix}retro` : null;
}

export function cardBackUrl(deckType) {
  const name = cardBackFileName(deckType);
  return name ? `./${CARDS_PATH}/${name}.png` : null;
}

export function aspectFor(deckType) {
  return DECKS[deckType]?.aspect ?? DECKS.TREVISANE.aspect;
}

// Every file name one deck can ask for. The back comes first because it is the single most drawn
// image a table has - every opponent holds a fan of them, the pile is one, and a deal is mostly
// backs - so on a slow line it is the one worth having before any face
export function deckFileNames(deckType) {
  const deck = DECKS[deckType];
  if (!deck) return [];
  const names = new Set();
  names.add(cardBackFileName(deckType));
  const seeds = deck.french ? FRENCH_SEEDS : ITALIAN_SEEDS;
  for (const seed of Object.keys(seeds)) {
    for (const cardType of deck.values) {
      names.add(cardFileName(deckType, Number(seed), cardType));
    }
  }
  if (deck.jokers) names.add(cardFileName(deckType, 0, JOKER_CARD_TYPE));
  return [...names].filter(Boolean);
}

// Every file name any deck can ask for, for the build time check
export function everyCardFileName() {
  const names = new Set();
  for (const deckType of Object.keys(DECKS)) {
    for (const name of deckFileNames(deckType)) names.add(name);
  }
  return [...names];
}

// The artwork of a whole deck, fetched the moment the deck is known instead of the moment a card
// is first shown. A flight lasts 260ms and a 37KB png over the wire lasts longer, so a card whose
// image is only asked for as it takes off spends most of its journey as a white rectangle - the
// one thing the Android client never does, because there the drawables come out of the apk.
// The BYTES only, deliberately: calling decode() here would also hold the whole deck decoded, and
// a deck is 27MB of pixels in the trevisane and 51MB in the napoletane. That much resident on a
// phone is bought back the worst possible way, as raster work in the middle of a flight. Decoding
// is left to the browser, which does it off the main thread and only at the size it is drawing.
// The elements are kept in the map: dropping them lets the browser drop the bytes with them.
const warmed = new Map();

export function warmDeck(deckType) {
  const names = deckFileNames(deckType);
  // An unknown deck is a server that does not name one yet: there is nothing to fetch, and the
  // table warms the real one itself when the game starts
  if (names.length === 0 || warmed.has(deckType) || typeof Image !== 'function') return;
  const images = [];
  warmed.set(deckType, images);
  // Never in the frame that asked for it, and at a lower priority than whatever is on screen:
  // when the table has to warm its own deck the hand and the trump ask for their artwork the
  // instant they are built, and fifty of these queued in front of them would only trade a white
  // card in flight for a white card in the hand
  const start = () => {
    for (const name of names) {
      const image = new Image();
      image.fetchPriority = 'low';
      image.src = `./${CARDS_PATH}/${name}.png`;
      images.push(image);
    }
  };
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => requestAnimationFrame(start));
  } else {
    start();
  }
}
